import { DependencyList, useEffect } from 'react';
import { useMutable } from './use-mutable';
import { debounce } from 'lodash';

/*eslint no-console: 0*/
/*eslint curly: 0*/

const DEBUG = false;

interface SmartScrollOptions {
  debounceTime?: number | null;
  /* Time in milliseconds after which if no page moment is detected, scrollIntoView will be called, by default it's 10x debounceTime.
  Set to 0 or null to disable fallback */
  hardFallback?: number | null;
  abortIfUserScrollsBefore?: boolean;
}

type ScrollTarget = React.RefObject<HTMLElement> | string;

// Helper function to get the element from either a selector or ref
const getScrollElement = (target: ScrollTarget): HTMLElement | null => {
  if (typeof target === 'string') {
    return document.querySelector(target);
  }
  return target.current;
};

// Helper function to calculate absolute position relative to document
const getAbsolutePosition = (element: HTMLElement) => {
  let absoluteTop = 0;
  let currentElement = element;

  while (currentElement && 'offsetTop' in currentElement) {
    absoluteTop += currentElement.offsetTop - currentElement.scrollTop;
    const offsetParent = currentElement.offsetParent as HTMLElement;
    if (!offsetParent) {
      break;
    }
    if (offsetParent !== document.body) {
      absoluteTop += offsetParent.scrollTop;
    }
    currentElement = offsetParent;
  }

  return {
    absoluteTop,
    absoluteBottom: absoluteTop + element.getBoundingClientRect().height,
  };
};

export const useSmartScroll = (
  /** React ref or a selector such as '#my-item-id' or '.pageHeader'
   */
  scrollTarget: ScrollTarget,
  pageHeightElementSelector: string,
  enable = true,
  options: SmartScrollOptions = {
    hardFallback: 100 * 10,
    debounceTime: 100,
    abortIfUserScrollsBefore: true,
  },
  /**
   * Optional list of additional React dependancies
   */
  deps: DependencyList = [],
) => {
  const [lastScrollOnHeight, setLastScrollOnHeight] = useMutable(-1);

  // Destructure with defaults only for undefined values
  const { hardFallback = 100 * 10, debounceTime = 100 } = options;

  let timeoutId: NodeJS.Timeout;
  let useFallback = true;
  useEffect(() => {
    let amountOfScrolls = 0;
    let amountOfScrollsFromSpaceBelowChanges = 0;
    let lastScrollSpaceBelow = -1;
    let hasFirstAutoScroll = false;
    let observer: MutationObserver;
    const pageHeights: { top: number; height: number; timestamp: number; spaceBelow?: number }[] = [];

    // Get the scrollable container
    const scrollContainer = document.querySelector(pageHeightElementSelector);
    if (!scrollContainer) {
      console.warn('Scroll container not found:', pageHeightElementSelector);
      return () => {
        // Return empty cleanup function
      };
    }

    // Custom scroll function that marks the event
    let autoScrollStart = 0;
    const smoothScrollIntoView = (element: HTMLElement) => {
      amountOfScrolls++;
      const scrollOptions: ScrollIntoViewOptions & { isSmartScroll?: boolean } = {
        behavior: 'smooth',
        block: 'start',
        isSmartScroll: true, // Mark this scroll as coming from our hook
      };
      element.scrollIntoView(scrollOptions);
      autoScrollStart = performance.now();
    };

    // Add scroll event listener to detect user scrolling on the container
    const handleUserScroll = (_event: Event) => {
      // Check if this is our auto-scroll
      const now = performance.now();
      const diff = now - autoScrollStart;
      if (diff < 1000) {
        return;
      }

      if (hasFirstAutoScroll || options.abortIfUserScrollsBefore) {
        if (DEBUG) console.log('User manually scrolled after auto-scroll, disconnecting observer');
        observer?.disconnect();
        scrollContainer.removeEventListener('scroll', handleUserScroll);
      }
    };

    if (enable) {
      scrollContainer.addEventListener('scroll', handleUserScroll);

      observer = new MutationObserver(
        debounce(() => {
          if (timeoutId != null) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }
          const contentHeight = document.querySelector(pageHeightElementSelector);
          const scrollElement = getScrollElement(scrollTarget);

          let absoluteTop = -1;
          let absoluteBottom = -1;
          let spaceBelow = -1;

          // Log the position of the target element
          if (scrollElement) {
            const rect = scrollElement.getBoundingClientRect();
            ({ absoluteTop, absoluteBottom } = getAbsolutePosition(scrollElement));

            // UNUSED Calculate document height and space below target
            const documentHeight = Math.max(
              document.body.scrollHeight,
              document.body.offsetHeight,
              document.documentElement.clientHeight,
              document.documentElement.scrollHeight,
              document.documentElement.offsetHeight,
            );

            spaceBelow = contentHeight?.scrollHeight - absoluteBottom;
            if (DEBUG)
              console.log('Target element position:', {
                // Document-relative positions
                documentTop: absoluteTop,
                documentLeft: absoluteBottom,
                documentHeight: documentHeight,
                spaceBelow: spaceBelow,
                isInViewport: rect.top >= 0 && rect.bottom <= window.innerHeight,
              });
          }

          pageHeights.push({
            top: absoluteTop,
            height: contentHeight?.scrollHeight,
            timestamp: Date.now(),
            spaceBelow: spaceBelow,
          });

          if (pageHeights.length >= 1) {
            if (scrollElement && (absoluteTop !== lastScrollOnHeight() || spaceBelow !== lastScrollSpaceBelow)) {
              if (absoluteTop === lastScrollOnHeight() && spaceBelow !== lastScrollSpaceBelow) {
                // will trigger scroll due to space below change
                amountOfScrollsFromSpaceBelowChanges++;
              }
              smoothScrollIntoView(scrollElement); // Use our custom scroll function
              setLastScrollOnHeight(absoluteTop);
              lastScrollSpaceBelow = spaceBelow;
              useFallback = false;

              if (!hasFirstAutoScroll) {
                hasFirstAutoScroll = true;
              }

              if (amountOfScrolls >= 20 || amountOfScrollsFromSpaceBelowChanges >= 2) {
                if (DEBUG) console.log('too many scrolls, stopping', amountOfScrolls);
                observer.disconnect();
                scrollContainer.removeEventListener('scroll', handleUserScroll);
                return;
              }
            } else if (scrollElement == null) {
              if (DEBUG) console.warn('couldnt scroll, element not found');
              // TODO
            }
          }
          if (pageHeights.length >= 3) {
            const lastThreeTops = pageHeights.slice(-3).map((entry) => entry.top);
            const lastThreeSpaceBelow = pageHeights.slice(-3).map((entry) => entry.spaceBelow);
            if (
              lastThreeTops.every((top) => top === lastThreeTops[0]) &&
              lastThreeSpaceBelow.every((spaceBelow) => spaceBelow === lastThreeSpaceBelow[0])
            ) {
              // Observer stopped as the last 3 heights are the same
              if (lastScrollOnHeight() === -1) {
                // last check is to make sure we have actually scrolled to the element, maybe the element is not rendered yet.
                if (DEBUG) console.log('not stopping, element not found yet', scrollElement);
                return;
              }
              if (DEBUG) console.log('stopping, 3 same height', scrollElement);
              observer.disconnect();
              scrollContainer.removeEventListener('scroll', handleUserScroll);
              return;
            }
          }
          if (useFallback && hardFallback !== 0 && hardFallback !== null) {
            timeoutId = setTimeout(() => {
              const scrollElement = getScrollElement(scrollTarget);
              if (scrollElement == null) {
                return;
              }

              if (DEBUG) console.log('will trigger scroll due to lack of page movements');
              smoothScrollIntoView(scrollElement);
              setLastScrollOnHeight(pageHeights[pageHeights.length - 1].top);
              lastScrollSpaceBelow = spaceBelow;
              useFallback = false;
            }, hardFallback ?? debounceTime * 10);
          }
        }, debounceTime),
      );

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    return () => {
      observer?.disconnect();
      scrollContainer?.removeEventListener('scroll', handleUserScroll);
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      pageHeights.length = 0;
    };
  }, [debounceTime, enable, pageHeightElementSelector, ...deps]);
};
